import HttpRequestError from "errors/HttpRequestError";
import EnvironmentUtils from "utils/EnviromentUtils";
import { plainToClass } from "class-transformer";
import { ClassType } from "class-transformer/ClassTransformer";
import { ErrorCode } from "constants/ErrorCode";
import { store } from "index";
import { logout } from "redux/actions/account";
const BASE_PATH = EnvironmentUtils.getApiUrl();

export default class RequestUtils {
  private constructor() {}

  static async post<T>(cls: ClassType<T>, path: string, data: any): Promise<T> {
    return await RequestUtils.fetchAndPlainToClass(cls, "POST", path, data);
  }

  static async get<T>(cls: ClassType<T>, path: string): Promise<T> {
    return await RequestUtils.fetchAndPlainToClass(cls, "GET", path);
  }

  static async stringList(path: string): Promise<string[]> {
    return await RequestUtils.fetchBase("GET", path);
  }

  static async stringListPost(path: string, data: any): Promise<string[]> {
    return await RequestUtils.fetchBase("POST", path, data);
  }

  static async count(path: string): Promise<number> {
    return await RequestUtils.fetchBase("GET", path);
  }

  static async countPost(path: string, data: any): Promise<number> {
    return await RequestUtils.fetchBase("POST", path, data);
  }

  static async put<T>(cls: ClassType<T>, path: string, data: any): Promise<T> {
    return await RequestUtils.fetchAndPlainToClass(cls, "PUT", path, data);
  }

  static async delete<T>(
    cls: ClassType<T>,
    path: string,
    data: any
  ): Promise<T> {
    return await RequestUtils.fetchAndPlainToClass(cls, "DELETE", path, data);
  }

  static async postArray<T>(
    cls: ClassType<T>,
    path: string,
    data: any
  ): Promise<T[]> {
    return await RequestUtils.fetchAndPlainToClassArray(
      cls,
      "POST",
      path,
      data
    );
  }

  static async getArray<T>(cls: ClassType<T>, path: string): Promise<T[]> {
    return await RequestUtils.fetchAndPlainToClassArray(cls, "GET", path);
  }

  static async getPlain(path: string) {
    return await RequestUtils.fetchBase("GET", path);
  }

  static async postPlain(path: string, data: any) {
    return await RequestUtils.fetchBase("POST", path, data);
  }

  static async putArray<T>(
    cls: ClassType<T>,
    path: string,
    data: any
  ): Promise<T[]> {
    return await RequestUtils.fetchAndPlainToClassArray(cls, "PUT", path, data);
  }

  static async deleteArray<T>(
    cls: ClassType<T>,
    path: string,
    data: any
  ): Promise<T[]> {
    return await RequestUtils.fetchAndPlainToClassArray(
      cls,
      "DELETE",
      path,
      data
    );
  }

  static async postVoid(path: string, data: any) {
    await RequestUtils.fetchBase("POST", path, data);
  }

  static async getVoid(path: string) {
    await RequestUtils.fetchBase("GET", path);
  }

  static async putVoid(path: string, data: any) {
    await RequestUtils.fetchBase("PUT", path, data);
  }

  static async deleteVoid(path: string, data: any) {
    await RequestUtils.fetchBase("DELETE", path, data);
  }

  public static async getBlob(url: string, withBasePath?: boolean) {
    const accessToken = store.getState().account?.accessToken;
    const res: Response = await RequestUtils.timeout(
      5 * 60 * 1000,
      fetch(withBasePath ? BASE_PATH + url : url, {
        method: "GET",
        mode: "cors",
        credentials: "include",
        headers: {
          // IEではGETがキャッシュされてしまうため
          "If-Modified-Since": "Thu, 01 Jun 1970 00:00:00 GMT",
          Authorization: `Bearer ${accessToken}`,
        },
      })
    );
    return await RequestUtils.handleBlobResponse(res);
  }

  private static async timeout(
    ms: number,
    promise: Promise<any>
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      setTimeout(() => reject(new HttpRequestError(ErrorCode.TIMEOUT, 0)), ms);
      promise.then(resolve, reject);
    });
  }

  private static async fetchAndPlainToClass<T>(
    cls: ClassType<T>,
    method: string,
    path: string,
    data: any = undefined
  ) {
    const json = await RequestUtils.fetchBase(method, path, data);

    return plainToClass(cls, json);
  }

  private static async fetchAndPlainToClassArray<T>(
    cls: ClassType<T>,
    method: string,
    path: string,
    data: any = undefined
  ) {
    const json: unknown[] = await RequestUtils.fetchBase(method, path, data);

    return plainToClass(cls, json);
  }

  private static async fetchBase(
    method: string,
    path: string,
    data: any = undefined,
    isFormDate = false
  ) {
    try {
      const accessToken = store.getState().account?.accessToken;
      const res: Response = await RequestUtils.timeout(
        1 * 60 * 1000,
        fetch(BASE_PATH + path, {
          method: method,
          mode: "cors",
          credentials: "include",
          headers: isFormDate
            ? undefined
            : {
                "Content-Type": "application/json; charset=utf-8",
                // IEではGETがキャッシュされてしまうため
                "If-Modified-Since": "Thu, 01 Jun 1970 00:00:00 GMT",
                Authorization: `Bearer ${accessToken}`,
              },
          body: data ? JSON.stringify(data) : data,
        })
      );
      return await RequestUtils.handleJsonResponse(res);
    } catch (error) {
      RequestUtils.alertForError(error);
      // @ts-ignore
      if (error.statusCode === 401) {
        // @ts-ignore
        store.dispatch(logout());
      }
      throw error;
    }
  }

  /**
   * ★未払いで 止まっている間は お支払い画面へ 連れて行く★（案三・2026-08-13）
   *
   * ★画面・導線・見た目は 一切 触っていない。★誤りの 出し方だけ。
   * legacy は 凍結しているので、それ以外は そのまま。
   *
   * なぜ 要るか …… サーバの文（「設定 → お支払い から…」）は 元から 出ていた。
   *   足りないのは 二つ ──
   *     一 ★画面ひとつが API を 何本も 叩くので 失敗の数だけ 出る★
   *       （実測 …… TOP で 8 回・お支払い画面で 3 回）
   *     二 案内した先へ 自分で 辿らないと いけない
   *
   * ★お支払い画面に 居る時は 動かさない★ …… でないと 読み込み → 402 → 読み込み で 回る。
   */
  private static readonly PAY_PATH = "/payment";
  private static readonly SUSPEND_EXPLAINED_KEY = "billingSuspendedExplained";
  public static readonly SUSPEND_REDIRECT_KEY = "billingSuspendedRedirect";

  private static alertForError(error: any): void {
    if (error?.statusCode === 402) {
      RequestUtils.goToPaymentPage(error?.message);
      return;
    }
    alert(`${error?.message}`);
  }

  /** 入り直したら また 一度だけ 理由を 出す（ログイン成功時に 呼ぶ）。 */
  public static resetBillingSuspendedNotice(): void {
    try {
      window.sessionStorage.removeItem(RequestUtils.SUSPEND_EXPLAINED_KEY);
    } catch (_e) {
      // プライベート閲覧などで 使えない事が ある。出せないだけで 支障は 無い。
    }
  }

  /** お支払い画面へ 連れて行かれた 直後かどうか（往復を 止める 印）。 */
  public static wasSentToPaymentBySuspension(): boolean {
    try {
      return (
        window.sessionStorage.getItem(RequestUtils.SUSPEND_REDIRECT_KEY) === "1"
      );
    } catch (_e) {
      return false;
    }
  }

  private static goToPaymentPage(message?: string): void {
    let explained = false;
    try {
      explained =
        window.sessionStorage.getItem(RequestUtils.SUSPEND_EXPLAINED_KEY) ===
        "1";
    } catch (_e) {
      explained = false;
    }
    if (!explained) {
      try {
        window.sessionStorage.setItem(RequestUtils.SUSPEND_EXPLAINED_KEY, "1");
      } catch (_e) {
        // 覚えられない時は 毎回 出る。黙って 飛ばすより 良い。
      }
      alert(
        message ||
          "お支払いが確認できないため、ご利用を一時停止しております。お支払い画面へ移動します。"
      );
    }
    if (window.location.pathname !== RequestUtils.PAY_PATH) {
      try {
        window.sessionStorage.setItem(RequestUtils.SUSPEND_REDIRECT_KEY, "1");
      } catch (_e) {
        // 印が 置けなくても 往復は しない（上の 判定で 止まる）
      }
      window.location.assign(RequestUtils.PAY_PATH);
    }
  }

  public static async postFormData<T>(
    cls: ClassType<T>,
    path: string,
    data: FormData
  ) {
    const res: Response = await RequestUtils.timeout(
      1 * 60 * 1000,
      fetch(BASE_PATH + path, {
        method: "POST",
        mode: "cors",
        credentials: "include",
        body: data,
      })
    );
    const json = await RequestUtils.handleJsonResponse(res);
    return plainToClass(cls, json);
  }

  public static async postFormDataMultipart<T>(
    cls: ClassType<T>,
    path: string,
    formData: FormData
  ): Promise<T> {
    const res: Response = await RequestUtils.timeout(
      1 * 60 * 1000,
      fetch(BASE_PATH + path, {
        method: "POST",
        mode: "cors",
        credentials: "include",
        headers: undefined,
        body: formData,
      })
    );
    const json = await RequestUtils.handleJsonResponse(res);
    return plainToClass(cls, json);
  }

  private static async handleJsonResponse(res: Response) {
    if (res.ok) {
      return res.json();
    }
    const data = await res.json();
    throw new HttpRequestError(data.message, res.status);
  }

  private static async handleBlobResponse(res: Response) {
    if (res.ok) {
      return res.blob();
    }
    const data = await res.json();
    throw new HttpRequestError(data.message, res.status);
  }
}
